/* The net under the route table.

   Without one, a route chunk that fails to download takes the entire app with
   it: React unwinds to the root, unmounts everything, and leaves an empty
   `<div id="root">`. No page, no tab bar, no way to get anywhere — the app
   simply is not there any more, and a reload lands on the same broken worker
   and does it again.

   That is the failure this exists for, so it does more than apologise:

   · a chunk error is almost always a build that moved underneath a stale
     service worker, so the first one triggers a repair — drop the worker, drop
     its caches, reload onto the network. Once per tab; see pwa/recover.

   · if the repair has already been spent, or the error was not a chunk error
     at all, the boundary renders a real screen with a way out. It sits inside
     `<main>` rather than around the shell precisely so the tab bar survives:
     one broken route must not cost the user the other four.

   · it is keyed on the pathname, so navigating anywhere resets it. A boundary
     that latches is a boundary that turns one bad route into a dead app. */

import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '../ui/EmptyState'
import { hasAttemptedRecovery, isChunkLoadError, recoverFromStaleBuild } from '../../pwa/recover'
import { captureError } from '../../lib/telemetry'

/** What the user sees while the repair runs, and if it cannot.
 *
 *  Split out as a function component so it can use `useTranslation` — a class
 *  boundary cannot, and hard-coded English on the one screen a user only ever
 *  reaches when something has already gone wrong is not the place to save a
 *  component. */
function RouteErrorScreen({ error }: { error: unknown }) {
  const { t } = useTranslation()

  /* A chunk error that still has a repair available ends in a reload, so the
     honest thing to show for the moment before that is what is happening —
     not an error the user is about to be taken away from. */
  const repairable = isChunkLoadError(error) && !hasAttemptedRecovery()
  const [repairing, setRepairing] = useState(repairable)

  useEffect(() => {
    if (!repairable) return
    let live = true
    void recoverFromStaleBuild().then((started) => {
      // `recoverFromStaleBuild` navigates when it succeeds, so this only runs
      // if it declined — in which case the manual screen is the right one.
      if (live && !started) setRepairing(false)
    })
    return () => {
      live = false
    }
  }, [repairable])

  if (repairing) {
    return (
      <EmptyState
        icon={<RefreshCw size={26} />}
        title={t('common.updating')}
        body={t('common.crashBody')}
      />
    )
  }

  const chunk = isChunkLoadError(error)

  return (
    <EmptyState
      icon={<TriangleAlert size={26} />}
      title={chunk ? t('common.crashTitle') : t('common.errorTitle')}
      body={chunk ? t('common.crashBody') : t('common.errorBody')}
      action={
        <button
          type="button"
          className="btn primary"
          /* A reload, not a soft retry. `React.lazy` caches the rejection for
             the life of the component, so re-rendering the same route would
             re-throw the same error without asking the network anything — a
             button that cannot work is worse than no button. */
          onClick={() => window.location.reload()}
        >
          {t('common.crashReload')}
        </button>
      }
    />
  )
}

interface Props {
  children: ReactNode
}

interface State {
  error: unknown
}

class Boundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    /* Logged rather than swallowed. This is the one class of failure that is
       invisible in analytics — the app is gone, so nothing it would have
       reported gets reported. */
    console.error('[hexclean] route failed to render', error, info.componentStack)

    /* And reported, which is the half a console line never was. React catches
       render errors itself, so none of these reach `window.onerror`; without
       this call the most visible failure the app has — a screen that goes
       blank — is also the only one nobody hears about.

       A chunk error is included rather than filtered out. It usually means a
       stale service worker and repairs itself on the next line, but a *rising*
       count of them is how a bad deploy announces itself, and that signal only
       exists if the repairable ones are counted too. */
    captureError(error, {
      kind: 'react',
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
      context: {
        chunk: isChunkLoadError(error),
        recoveryAvailable: !hasAttemptedRecovery(),
      },
    })
  }

  override render(): ReactNode {
    if (this.state.error !== null) return <RouteErrorScreen error={this.state.error} />
    return this.props.children
  }
}

export function RouteBoundary({ children }: Props) {
  const { pathname } = useLocation()
  return <Boundary key={pathname}>{children}</Boundary>
}
